import { formatDateForUrl } from '@shared/utils/dateHelpers';

type InvalidAndUnknown = { valid: false; value: unknown };
type Valid<T> = { valid: true; value: T };

const processDestination = (str: unknown): InvalidAndUnknown | Valid<string> => {
  if (typeof str !== 'string') {
    return { valid: false, value: str };
  }
  return { valid: true, value: str };
};

const processLatLngValue = (str: unknown): InvalidAndUnknown | Valid<number> => {
  if (typeof str !== 'string') {
    return { valid: false, value: str };
  }
  const value = parseFloat(str);
  if (isNaN(value)) {
    return { valid: false, value: str };
  }
  if (value < -180 || value > 180) {
    return { valid: false, value: str };
  }
  return { valid: true, value };
};

const processDate = (str: unknown): InvalidAndUnknown | Valid<{ year: number; month: number; day: number }> => {
  if (typeof str !== 'string') {
    return { valid: false, value: str };
  }
  const match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) {
    return { valid: false, value: str };
  }
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return { valid: false, value: str };
  }
  if (isNaN(new Date(`${formatDateForUrl({ year, month, day })}T00:00:00`).getTime())) {
    return { valid: false, value: str };
  }
  return { valid: true, value: { year, month, day } };
};

const processRooms = (str: unknown): InvalidAndUnknown | Valid<Array<{ adults: number; childrenAges: number[] }>> => {
  if (typeof str !== 'string') {
    return { valid: false, value: str };
  }
  const roomsOutput: Array<{ adults: number; childrenAges: number[] }> = [];
  const rooms = str.split(',');
  for (const room of rooms) {
    // room is a ':' separated list of occupants
    const occupants = room.split(':');
    const childrenAges: number[] = [];
    let adults = 0;
    for (const occupant of occupants) {
      // occupant is either a\d or c\d meaning \d adults or child aged \d
      const match = occupant.match(/^a(\d+)|c(\d+)$/);
      if (!match) {
        return { valid: false, value: str };
      }
      if (match[1]) {
        const count = parseInt(match[1], 10);
        // Bad number for adults
        if (isNaN(count)) {
          return { valid: false, value: str };
        }
        adults += count;
      } else if (match[2]) {
        // Bad number for child age
        const age = parseInt(match[2], 10);
        if (isNaN(age)) {
          return { valid: false, value: str };
        }
        childrenAges.push(age);
      }
    }
    roomsOutput.push({ adults, childrenAges });
  }
  return { valid: true, value: roomsOutput };
};

const formatRoomsForUrl = (rooms: { adults: number; childrenAges: number[] }[]): string => {
  return rooms
    .map(room => {
      const children = room.childrenAges.length > 0 ? `:${room.childrenAges.map(age => `c${age}`).join(':')}` : '';
      return `a${room.adults}${children}`;
    })
    .join(',');
};

export { processDestination, processLatLngValue, processDate, processRooms, formatRoomsForUrl };
